import { ThemeContext } from '@hb/react'
import { InputSize } from '@hb/shared'
import { useContext } from 'react'

// Single source of truth for input sizing — the `size` prop ('xs'|'sm'|'md'|'lg', default 'md')
// is the one knob controlling font size, padding, and the spacing within/between inputs.
//
// Values are calibrated so that:
//   - `md` reproduces the previous `theme='detailed'` appearance exactly (the common default), and
//   - `sm` reproduces the previous `theme='basic'` appearance,
// so existing forms are visually unchanged (see `useInputSize` fallback below).

export interface InputSizeTokens {
  /** Font size for the input's text value. */
  fontSize: string
  /** Horizontal padding inside the input element itself (baseInputStyle / baseInputPaddingX). */
  innerPaddingX: string
  /** Horizontal padding of the input container/border box (Chakra spacing unit). */
  containerPaddingX: string
  /** Vertical padding of the input container/border box (Chakra spacing unit). */
  containerPaddingY: string
  /** Chakra font-size token for the field placeholder shown above the input. */
  placeholderFontSize: string
  /** Chakra font-size token for hint text. */
  hintFontSize: string
  /** Font size for the floating/active label. */
  labelFontSize: string
  /**
   * Font sizes for a FieldMap section header (the map's `name`), keyed by nesting level.
   * Each is a touch larger than `placeholderFontSize` and scales with the input size.
   * `root` = top-level form section, `nested` = depth 1, `sub` = deeper maps.
   */
  fieldMapFontSize: { root: string; nested: string; sub: string }
  /** Gap between fields sharing a row (Chakra spacing unit). */
  rowGap: number
  /** Vertical space inserted between consecutive fields in a field map. */
  bottomSpacer: string
  /** Chakra size for checkbox/switch/radio controls. */
  checkboxSize: 'sm' | 'md' | 'lg'
  /** Chakra size for buttons rendered alongside inputs (e.g. list "+ Add" button). */
  buttonSize: 'xs' | 'sm' | 'md' | 'lg'
}

export const INPUT_SIZE_TOKENS: Record<InputSize, InputSizeTokens> = {
  xs: {
    fontSize: '0.6875rem', // 11px
    innerPaddingX: '0.1rem',
    containerPaddingX: '0.25rem',
    containerPaddingY: '0.125rem',
    placeholderFontSize: 'xs',
    hintFontSize: 'xs',
    labelFontSize: '11px',
    fieldMapFontSize: { root: '0.9375rem', nested: '0.875rem', sub: '0.8125rem' },
    rowGap: 2,
    bottomSpacer: '3px',
    checkboxSize: 'sm',
    buttonSize: 'xs',
  },
  sm: {
    // Matches previous theme='basic': same 12px font, tighter padding than md.
    fontSize: '0.75rem', // 12px
    innerPaddingX: '0.15rem',
    containerPaddingX: '0.375rem',
    containerPaddingY: '0.25rem',
    placeholderFontSize: 'xs',
    hintFontSize: 'sm',
    labelFontSize: '12px',
    fieldMapFontSize: { root: '0.9375rem', nested: '0.875rem', sub: '0.8125rem' },
    rowGap: 3,
    bottomSpacer: '4px',
    checkboxSize: 'sm',
    buttonSize: 'xs',
  },
  md: {
    // Matches previous theme='detailed' (the default look).
    fontSize: '0.875rem', // 14px
    innerPaddingX: '0.25rem',
    containerPaddingX: '0.625rem',
    containerPaddingY: '0.375rem',
    placeholderFontSize: 'sm',
    hintFontSize: 'sm',
    labelFontSize: '13px',
    fieldMapFontSize: { root: '1.0625rem', nested: '1rem', sub: '0.9375rem' },
    rowGap: 3,
    bottomSpacer: '6px',
    checkboxSize: 'md',
    buttonSize: 'sm',
  },
  lg: {
    fontSize: '1rem', // 16px
    innerPaddingX: '0.35rem',
    containerPaddingX: '0.75rem',
    containerPaddingY: '0.5rem',
    placeholderFontSize: 'md',
    hintFontSize: 'md',
    labelFontSize: '14px',
    fieldMapFontSize: { root: '1.1875rem', nested: '1.125rem', sub: '1.0625rem' },
    rowGap: 4,
    bottomSpacer: '8px',
    checkboxSize: 'lg',
    buttonSize: 'sm',
  },
}

export const getInputSizeTokens = (size: InputSize = 'md'): InputSizeTokens =>
  INPUT_SIZE_TOKENS[size] ?? INPUT_SIZE_TOKENS.md

/**
 * Resolve the effective input size. Precedence: explicit `override` (e.g. `field.size`) →
 * form-wide `inputSize` from ThemeContext → a fallback derived from the legacy `theme`
 * ('basic' → 'sm', otherwise 'md') so pre-existing forms remain visually unchanged.
 */
export const useInputSize = (override?: InputSize): InputSize => {
  const { theme, inputSize } = useContext(ThemeContext)
  return override ?? inputSize ?? (theme === 'basic' ? 'sm' : 'md')
}
